/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ICommandBarItemProps } from '@fluentui/react/lib/components/CommandBar';
import { IColumn } from '@fluentui/react/lib/components/DetailsList';
import { Link } from '@fluentui/react/lib/Link';
import { Stack } from '@fluentui/react/lib/Stack';
import { getId } from '@fluentui/react/lib/Utilities';
import { Property } from 'csstype';
import orderBy from 'lodash.orderby';
import React from 'react';
import { defineMessages } from 'react-intl';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { LuisText } from 'src/core/components/LuisText';
import { PillContainer } from 'src/core/components/PillContainer';
import { SortKeyType } from 'src/core/components/TableProps';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useZoomLevel } from 'src/core/hooks/useZoomLevel';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { ModelEvaluationStore } from 'src/modules/modelDetails/stores/ModelEvaluationStore';
import { CustomPerformanceMetrics, ModelEvaluationTable } from 'src/modules/modelGuidance/components/ModelEvaluationTable';
import { ModelPerformanceFiltersBlade } from 'src/modules/modelGuidance/components/ModelPerformanceFiltersBlade';
import { handleGetLabel, SchemaType } from 'src/modules/modelGuidance/modelGuidanceUtils/modelGuidanceUtils';
import { ModelGuidanceStore } from 'src/modules/modelGuidance/stores/ModelGuidanceStore';

export type ModelEvaluationTabProps = {
    pivotName?: SchemaType;
    isExtractionProject: boolean;
    isEvaluationDataDisplayable: boolean;
    modelGuidanceStore: ModelGuidanceStore;
    onRenderOptions?: () => React.ReactNode;
    modelEvaluationStore: ModelEvaluationStore;
};

const messages = defineMessages({
    classificationTitle: {
        id: 'modelGuidance.modelEvaluationTab.classificationTitle',
        defaultMessage: 'Class performance'
    },
    extractionTitle: {
        id: 'modelGuidance.modelEvaluationTab.extractionTitle',
        defaultMessage: 'Entity performance'
    },
    className: {
        id: 'modelGuidance.modelEvaluationTab.className',
        defaultMessage: 'Class name'
    },
    entityName: {
        id: 'modelGuidance.modelEvaluationTab.entityName',
        defaultMessage: 'Entity name'
    },
    intentName: {
        id: 'modelGuidance.modelEvaluationTab.intentName',
        defaultMessage: 'Intent name'
    },
    emptyMessage: {
        id: 'modelGuidance.modelEvaluationTab.emptyMessage',
        defaultMessage: 'No {modelType} available'
    },
    tabDescription: {
        id: 'modelGuidance.modelEvaluationTab.tabDescription',
        defaultMessage:
            'This is a snapshot of how your model performed during testing. The metrics here are static and tied to your model, so they won’t update until you train again.'
    },
    filter: { id: 'modelGuidance.modelEvaluationTab.filter', defaultMessage: 'Filter' },
    filtersApplied: {
        id: 'modelGuidance.modelEvaluationTab.filtersApplied',
        defaultMessage: 'Filtered by:'
    },
    clearAll: {
        id: 'modelGuidance.modelEvaluationTab.clearAll',
        defaultMessage: 'Clear all'
    },
    clearAllAriaLabel: {
        id: 'modelGuidance.modelEvaluationTab.clearAllAriaLabel',
        defaultMessage: 'Clear all filters'
    }
});

const filtersTheme = getScopedTheme('Filters');
const { styleDiv } = getStylistV2('ModelDetailsPage');

const ModelEvaluationTableWindow = styleDiv('ModelEvaluationTableWindow', { height: '100%', overflowY: 'auto' });

export const ModelEvaluationTab = (props: ModelEvaluationTabProps) => {
    const {
        isExtractionProject,
        pivotName,
        modelGuidanceStore,
        modelEvaluationStore,
        isEvaluationDataDisplayable,
        onRenderOptions
    } = props;

    const { localize } = useLocalization();
    const { serviceName } = getPortalFromUrl();
    const { isAccessibleMode } = useZoomLevel();
    const filterItemIdRef = React.useRef<string>(getId());
    const [isFilterPaneHidden, setIsFilterPaneHidden] = React.useState(true);
    const [filterItems, updateFilterItems] = React.useState(
        modelEvaluationStore.performanceMetricsBreakdown
            .filter(metric => metric.name !== LuisConstants.CustomText.NoneLabelInConfusionMatrix)
            .map(metric => ({
                isChecked: false,
                labelName: metric.name
            }))
    );

    React.useEffect(() => {
        updateFilterItems(
            modelEvaluationStore.performanceMetricsBreakdown
                .filter(metric => metric.name !== LuisConstants.CustomText.NoneLabelInConfusionMatrix)
                .map(metric => ({
                    isChecked: false,
                    labelName: metric.name
                }))
        );
    }, [modelEvaluationStore.performanceMetricsBreakdown]);

    const [sortKey, setSortKey] = React.useState<SortKeyType<CustomPerformanceMetrics>>({ key: 'name', order: 'asc' });

    const tableItems = React.useMemo(
        () =>
            orderBy(
                modelEvaluationStore.performanceMetricsBreakdown
                    .map(metric => ({
                        ...metric,
                        ...(modelGuidanceStore?.labelsDistribution.find(labelDist => labelDist.labelName === metric.name)
                            ?.labelsDistribution ?? {
                            Train: null,
                            Test: null
                        })
                    }))
                    .filter(
                        metric =>
                            metric.name !== LuisConstants.CustomText.NoneLabelInConfusionMatrix &&
                            (filterItems.some(item => item.isChecked)
                                ? filterItems.find(item => item.labelName === metric.name).isChecked
                                : true)
                    ),
                [sortKey.key],
                [sortKey.order]
            ),
        [modelGuidanceStore?.labelsDistribution, modelEvaluationStore.performanceMetricsBreakdown, filterItems, sortKey.key, sortKey.order]
    );

    const onSortColumn = (c: IColumn) =>
        setSortKey({ key: c.key as keyof CustomPerformanceMetrics, order: c.isSortedDescending ? 'asc' : 'desc' });

    const onRemoveFilter = (labelName: string) => {
        updateFilterItems(filterItems.map(item => (item.labelName === labelName ? { labelName, isChecked: false } : item)));
    };

    const onClearAllFilters = () => {
        updateFilterItems(filterItems.map(item => ({ labelName: item.labelName, isChecked: false })));
    };

    const getFilteredLabels = () => (
        <Stack verticalAlign="center" horizontal tokens={{ childrenGap: 5 }}>
            <LuisText aria-label={localize(messages.filtersApplied)}>{localize(messages.filtersApplied)}</LuisText>

            <PillContainer
                allowAddPills={false}
                pills={filterItems
                    .filter(item => item.isChecked)
                    .map((item, index) => ({
                        index,
                        name: item.labelName,
                        pillKey: `${item.labelName}-${index}`,
                        onRemovePill: () => onRemoveFilter(item.labelName),
                        onRenderPillContent: () => (
                            <LuisText title={item.labelName} fontWeight={filtersTheme.boldFontWeight as Property.FontWeight}>
                                {item.labelName}
                            </LuisText>
                        )
                    }))}
            />
            <Link as="button" onClick={() => onClearAllFilters()}>
                <span aria-label={localize(messages.clearAllAriaLabel)}>{localize(messages.clearAll)}</span>
            </Link>
        </Stack>
    );

    const commandBarFarItems: ICommandBarItemProps[] = [
        {
            key: 'filter',
            id: filterItemIdRef.current,
            text: localize(messages.filter),
            iconProps: { iconName: 'Filter' },
            onClick: () => setIsFilterPaneHidden(false)
        }
    ];
    const getModelName = () => {
        switch (pivotName) {
            case 'Entities':
                return messages.entityName;
            case 'Intents':
                return messages.intentName;
            default:
        }
    };

    return (
        <Stack tokens={{ childrenGap: 20 }} data-automation-id="evaluationTab">
            <LuisCommandBar items={[]} farItems={commandBarFarItems} />

            <Stack tokens={{ childrenGap: 10 }}>
                <LuisText>{localize(messages.tabDescription)}</LuisText>

                {onRenderOptions && onRenderOptions()}

                {filterItems.some(item => item.isChecked) && getFilteredLabels()}

                {isEvaluationDataDisplayable && (
                    <ModelEvaluationTableWindow data-is-scrollable={!isAccessibleMode}>
                        <ModelEvaluationTable
                            sortKey={sortKey}
                            items={tableItems}
                            onSortColumn={onSortColumn}
                            nameColumnTitle={localize(
                                pivotName ? getModelName() : isExtractionProject ? messages.entityName : messages.className
                            )}
                            emptyPlaceholder={localize(messages.emptyMessage, {
                                modelType: localize(handleGetLabel(pivotName, serviceName, false))
                            })}
                        />
                    </ModelEvaluationTableWindow>
                )}
            </Stack>

            <ModelPerformanceFiltersBlade
                pivotName={pivotName}
                isOpen={!isFilterPaneHidden}
                labelsToFilter={filterItems}
                isExtractionProject={isExtractionProject}
                onDismiss={() => setIsFilterPaneHidden(true)}
                onFiltersChange={filters => updateFilterItems(filters)}
            />
        </Stack>
    );
};
